import { Navigate, Route, Routes, useNavigate } from "react-router";
import { useCallback, useEffect, useState } from "react";
import { clearToken, getToken } from "./lib/auth";
import { SessionProvider } from "./lib/session";
import { isPlatformAdmin } from "./lib/scopes";
import ErrorBoundary from "./components/ErrorBoundary";
import ScopeGate from "./components/ScopeGate";
import Sidebar from "./components/Sidebar";
import ConnectRoute from "./routes/ConnectRoute";
import OAuthCallbackRoute from "./routes/OAuthCallbackRoute";
import SystemRoute from "./routes/SystemRoute";
import OrgsRoute from "./routes/OrgsRoute";
import RealmsRoute from "./routes/RealmsRoute";
import RealmLayout from "./routes/RealmLayout";
import RealmOverviewRoute from "./routes/RealmOverviewRoute";
import RealmModelsRoute from "./routes/RealmModelsRoute";
import RealmRenderProfilesRoute from "./routes/RealmRenderProfilesRoute";
import DeploymentConfigRoute from "./routes/DeploymentConfigRoute";
import ServiceAccountsRoute from "./routes/ServiceAccountsRoute";
import MeRoute from "./routes/MeRoute";

export default function App() {
  const [authed, setAuthed] = useState<boolean>(!!getToken());
  const nav = useNavigate();

  useEffect(() => {
    const onStorage = () => setAuthed(!!getToken());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  function logout() {
    clearToken();
    setAuthed(false);
    nav("/connect");
  }

  // The token is already cleared by the session provider at this point; this only
  // flips the shell so the connect screen renders.
  const onSessionLost = useCallback(() => {
    setAuthed(false);
    nav("/connect");
  }, [nav]);

  if (!authed) {
    return (
      <div className="min-h-screen">
        <header className="border-b border-kw-border-subtle px-6 py-3">
          <span className="text-kw-l3 font-mono text-sm">kw://admin</span>
        </header>
        <Routes>
          <Route
            path="/connect"
            element={<ConnectRoute onConnected={() => setAuthed(true)} />}
          />
          <Route
            path="/callback"
            element={<OAuthCallbackRoute onConnected={() => setAuthed(true)} />}
          />
          <Route path="*" element={<Navigate to="/connect" replace />} />
        </Routes>
      </div>
    );
  }

  return (
    <SessionProvider onSessionLost={onSessionLost}>
      <div className="min-h-screen grid grid-cols-[220px_1fr]">
        <Sidebar onLogout={logout} />
        <main className="overflow-auto">
          <ErrorBoundary>
            <Routes>
              <Route path="/" element={<Navigate to="/realms" replace />} />
              <Route
                path="/system"
                element={
                  <ScopeGate require={isPlatformAdmin} redirectTo="/realms">
                    <SystemRoute />
                  </ScopeGate>
                }
              />
              <Route
                path="/organizations"
                element={
                  <ScopeGate require={isPlatformAdmin} redirectTo="/realms">
                    <OrgsRoute />
                  </ScopeGate>
                }
              />
              <Route path="/realms" element={<RealmsRoute />} />
              <Route path="/realms/:realmId" element={<RealmLayout />}>
                <Route index element={<RealmOverviewRoute />} />
                <Route path="llm" element={<RealmModelsRoute />} />
                <Route path="styles" element={<RealmRenderProfilesRoute />} />
              </Route>
              <Route
                path="/service-accounts"
                element={
                  <ScopeGate require={isPlatformAdmin} redirectTo="/realms">
                    <ServiceAccountsRoute />
                  </ScopeGate>
                }
              />
              <Route
                path="/config"
                element={
                  <ScopeGate require={isPlatformAdmin} redirectTo="/realms">
                    <DeploymentConfigRoute />
                  </ScopeGate>
                }
              />
              <Route path="/me" element={<MeRoute />} />
              <Route path="*" element={<Navigate to="/realms" replace />} />
            </Routes>
          </ErrorBoundary>
        </main>
      </div>
    </SessionProvider>
  );
}
